import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  myBrandDrawerFatigueDataQueryOptions,
  type DrilldownPageFiltersInput,
  type DrilldownQuerySection,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"
import type { FatigueGranularity } from "../lib/my-brand-drawer-fatigue-catalog"
import type { PerformanceKpiId } from "../lib/my-brand-drawer-performance-catalog"
import {
  myBrandDrawerFatigueCacheKey,
  peekCachedMyBrandDrawerFatigueData,
  setCachedMyBrandDrawerFatigueData,
  touchCachedMyBrandDrawerFatigueData,
} from "./useMyBrandDrawerFatigueDataCache"

const METRICS_DEBOUNCE_MS = 200

export function useMyBrandDrawerFatigueDataSection(
  creative: MyBrandCreativeCardModel | null,
  open: boolean,
  activeTab: string,
  myBrandFilters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
  selectedMetrics: PerformanceKpiId[],
  granularity: FatigueGranularity,
): {
  section: DrilldownQuerySection | null
  isLoading: boolean
  isError: boolean
} {
  const hash = creative?.hash ?? ""
  const adId = creative?.adId ?? null

  const [debouncedMetrics, setDebouncedMetrics] = useState(selectedMetrics)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedMetrics(selectedMetrics), METRICS_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [selectedMetrics])

  const cacheKey =
    hash && debouncedMetrics.length > 0
      ? myBrandDrawerFatigueCacheKey(
          hash,
          adId,
          myBrandFilters,
          drilldownFilters,
          granularity,
          debouncedMetrics,
        )
      : ""

  const cached = useMemo(
    () => (cacheKey ? peekCachedMyBrandDrawerFatigueData(cacheKey) : undefined),
    [cacheKey],
  )

  useEffect(() => {
    if (cacheKey && cached) touchCachedMyBrandDrawerFatigueData(cacheKey)
  }, [cacheKey, cached])

  const shouldFetch =
    open && activeTab === "fatigue" && !!cacheKey && debouncedMetrics.length > 0 && !cached

  const { data, isLoading, isError } = useQuery(
    myBrandDrawerFatigueDataQueryOptions(
      hash,
      adId,
      myBrandFilters,
      drilldownFilters,
      debouncedMetrics,
      granularity,
      shouldFetch,
    ),
  )

  useEffect(() => {
    if (data && cacheKey) setCachedMyBrandDrawerFatigueData(cacheKey, data)
  }, [data, cacheKey])

  const section = cached ?? data ?? null

  return { section, isLoading: shouldFetch && isLoading, isError }
}
